import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType } from '../types/auth';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Don't send any token - just let the server check for the HTTP-only cookie
      const res = await fetch('/api/account', {
        method: 'GET',
        credentials: 'same-origin' // Important to include cookies
      });
      
      if (res.ok) {
        const data = await res.json();
        setIsAuthenticated(true);
        setUser(data);
      } else {
        // Only clear localStorage if auth failed
        localStorage.removeItem('googleToken');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error during authentication check:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (response: any) => {
    try {
      const res = await fetch('/oauth/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: response.credential }),
      });
      const data = await res.json();
      if (data.success) {
        // Token is valid
        localStorage.setItem('googleToken', response.credential);
        
        // Refresh account info from the account API to get complete user data
        await checkAuthStatus();
      } else {
        // Token is invalid
        console.error('Invalid token');
      }
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  const logout = async () => {
    try {
      // Call the logoff API endpoint
      await fetch('/api/logoff', {
        method: 'POST',
        credentials: 'same-origin'
      });
      
      // Clear local storage
      localStorage.removeItem('googleToken');
      
      // Update auth state
      setIsAuthenticated(false);
      setUser(null);
      
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
